import type { LanguageCode } from "@/components/translations";
import type { ModuleItemType } from '@masters-club/types';

export type { ModuleItemType };

export interface ModuleTranslation {
  id: string;
  moduleId: string;
  languageCode: LanguageCode;
  title: string;
  description: string | null;
}

export interface ModuleItemTranslation {
  id: string;
  itemId: string;
  languageCode: LanguageCode;
  title: string;
  content: string | null;
}

export const MODULE_ITEM_TYPE_OPTIONS: { value: ModuleItemType; label: string }[] = [
  { value: "VIDEO", label: "Видео" },
  { value: "AUDIO", label: "Аудио" },
  { value: "ARTICLE", label: "Статья" },
  { value: "HOMEWORK", label: "Домашнее задание" },
  { value: "BOOK", label: "Книга" },
];

export type VideoProcessingStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'ERROR';

export type HomeworkType = 'ONE_TIME' | 'DAILY_STREAK' | 'PERIOD';

export interface HomeworkConfig {
  homeworkType: HomeworkType;
  deadline: string | null;
  streakDays: number | null;
  periodHours: number | null;
}

export interface ModuleItem {
  id: string;
  moduleId: string;
  type: ModuleItemType;
  order: number;
  videoDuration: number | null;
  videoUrl: string | null;
  isPreview: boolean;
  isProtected: boolean;
  processingStatus: VideoProcessingStatus | null;
  processingError: string | null;
  hlsPlaylistUrl: string | null;
  homeworkConfig: HomeworkConfig | null;
  bookId: number | null;
  posterUrl: string | null;
  posterFileId: number | null;
  /** ISO-строка даты открытия урока. null = открыт сразу / наследует от модуля. */
  availableFrom: string | null;
  createdAt: string;
  updatedAt: string;
  translations: ModuleItemTranslation[];
}

export interface Module {
  id: string;
  courseId: string;
  order: number;
  /** ISO-строка даты открытия модуля. null = открыт сразу. */
  availableFrom: string | null;
  createdAt: string;
  updatedAt: string;
  translations: ModuleTranslation[];
  items: ModuleItem[];
}

/**
 * Конвертирует ISO-строку (UTC) в формат для input[type=datetime-local]
 * (`YYYY-MM-DDTHH:mm` без timezone). Возвращает локальное время браузера.
 */
export function toDatetimeLocal(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function getModuleTitle(module: Module, lang: LanguageCode = "ru"): string {
  const translations = module.translations ?? [];
  return translations.find((t) => t.languageCode === lang)?.title ?? translations[0]?.title ?? `Модуль ${module.order + 1}`;
}

export function getItemTitle(item: ModuleItem, lang: LanguageCode = "ru"): string {
  const translations = item.translations ?? [];
  return translations.find((t) => t.languageCode === lang)?.title ?? translations[0]?.title ?? `Урок ${item.order + 1}`;
}

// --- Draft types for wizard (offline mode) ---

export interface ModuleDraftTranslation {
  languageCode: string;
  title: string;
  description?: string | null;
}

export interface ModuleItemDraftTranslation {
  languageCode: string;
  title: string;
  content?: string | null;
}

export interface ModuleItemDraft {
  tempId: string;
  type: ModuleItemType;
  order: number;
  isPreview: boolean;
  videoUrl: string | null;
  videoDuration: number | null;
  bookId: number | null;
  translations: ModuleItemDraftTranslation[];
}

export interface ModuleDraft {
  tempId: string;
  order: number;
  translations: ModuleDraftTranslation[];
  items: ModuleItemDraft[];
}

export function createEmptyModuleDraft(order: number): ModuleDraft {
  return {
    tempId: crypto.randomUUID(),
    order,
    translations: [{ languageCode: 'ru', title: '', description: null }],
    items: [],
  };
}

export function createEmptyItemDraft(order: number, type: ModuleItemType = 'VIDEO'): ModuleItemDraft {
  return {
    tempId: crypto.randomUUID(),
    type,
    order,
    isPreview: false,
    videoUrl: null,
    videoDuration: null,
    bookId: null,
    translations: [{ languageCode: 'ru', title: '', content: null }],
  };
}

export function getModuleDraftTitle(mod: ModuleDraft, lang = 'ru'): string {
  const t = mod.translations.find((tr) => tr.languageCode === lang);
  return t?.title || mod.translations[0]?.title || `Модуль ${mod.order + 1}`;
}

export function getItemDraftTitle(item: ModuleItemDraft, lang = 'ru'): string {
  const t = item.translations.find((tr) => tr.languageCode === lang);
  return t?.title || item.translations[0]?.title || `Урок ${item.order + 1}`;
}
